import React, { useEffect, useState } from "react";
import {
  EntPageShell, cardStyle, panelHeader, goldBtn, ghostBtn,
  ENT_COLOR, SeverityPill, Empty, ErrorMsg,
} from "./_shell";
import {
  messagingApi, schedulingApi, medicalApi, complianceApi,
  documentsApi, alertsEntApi,
} from "../../lib/enterpriseApi";
import { auth } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

// ==================================================== §1 MessagingCenter
export const MessagingCenter = () => {
  const [messages, setMessages] = useState([]);
  const [recipient, setRecipient] = useState("athlete@fsu.demo");
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { const r = await messagingApi.history(); setMessages(r.messages || []); }
    catch (e) { setError("Sign in to view messages."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to use Messaging."); }, []);

  const send = async () => {
    if (!content.trim() || busy) return;
    setBusy(true);
    try {
      await messagingApi.send({ recipient_id: recipient, content, message_type: "text" });
      setContent(""); await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail?.detail || "Send failed.");
    } finally { setBusy(false); }
  };

  return (
    <EntPageShell title="Messaging Center" testId="messaging-center"
      lede="Direct + group communications across coaches, trainers, athletes, and compliance staff. Push / email / SMS provider-agnostic — wire your Firebase / SendGrid / Twilio keys to swap demo for live delivery.">
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="messaging-inbox">
          <div style={panelHeader}>
            <span>Inbox &amp; Sent</span>
            <button data-testid="messaging-refresh" style={ghostBtn(true)} onClick={refresh}>Refresh</button>
          </div>
          {error && <ErrorMsg message={error} />}
          {!error && messages.length === 0 && <Empty note="No messages yet. Send your first below." testid="messaging-empty" />}
          {messages.map((m) => (
            <div key={m.id} data-testid="messaging-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0" }}>
              <div style={{ fontSize: 11, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>
                {m.sender_id} → {m.recipient_id || m.group_id}
              </div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{m.content}</div>
              <div style={{ fontSize: 10, color: "rgba(244,239,230,0.45)", marginTop: 2 }}>
                {fmtDate(m.sent_at)} · {m.delivery_status}
              </div>
            </div>
          ))}
        </div>
        <div style={cardStyle} data-testid="messaging-compose">
          <div style={panelHeader}>Compose</div>
          <label style={{ fontSize: 11, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>Recipient</label>
          <input data-testid="messaging-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)}
            style={{ width: "100%", padding: 10, marginTop: 4, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }} />
          <label style={{ fontSize: 11, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1, marginTop: 12, display: "block" }}>Message</label>
          <textarea data-testid="messaging-content" value={content} onChange={(e) => setContent(e.target.value)} rows={5}
            style={{ width: "100%", padding: 10, marginTop: 4, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4, fontFamily: "inherit" }} />
          <div style={{ marginTop: 14, textAlign: "right" }}>
            <button data-testid="messaging-send" style={goldBtn()} onClick={send} disabled={busy}>Send</button>
          </div>
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §2 SchedulingCenter
export const SchedulingCenter = () => {
  const [events, setEvents] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auth.token()) { setError("Sign in to view schedule."); return; }
    Promise.all([schedulingApi.list(), schedulingApi.conflicts(), schedulingApi.facilities()])
      .then(([e, c, f]) => { setEvents(e.events || []); setConflicts(c.conflicts || []); setFacilities(f.facilities || []); })
      .catch(() => setError("Could not load schedule."));
  }, []);

  return (
    <EntPageShell title="Scheduling" testId="scheduling-center"
      lede="Practice, game, travel, facility, and staff scheduling with conflict detection. Game schedules sync from the public-source Crawl Engine.">
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="schedule-list-card">
          <div style={panelHeader}>Upcoming events ({events.length})</div>
          {error && <ErrorMsg message={error} />}
          {!error && events.length === 0 && <Empty note="No events scheduled. Use POST /api/schedule/create." />}
          {events.map((e) => (
            <div key={e.id} data-testid="schedule-event-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{e.title}</div>
                  <div style={{ fontSize: 11, color: "rgba(244,239,230,0.6)" }}>
                    {e.sport.toUpperCase()} · {fmtDate(e.start_time)} · facility {e.facility_id}
                  </div>
                </div>
                {e.conflict_flag && <SeverityPill severity="high" data-testid="schedule-conflict-pill" />}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gap: 14 }}>
          <div style={cardStyle} data-testid="schedule-conflicts-card">
            <div style={panelHeader}>Conflicts ({conflicts.length})</div>
            {conflicts.length === 0 ? <Empty note="No active conflicts." testid="schedule-no-conflicts" /> :
              conflicts.map((c) => (
                <div key={c.id} style={{ fontSize: 12, padding: "6px 0", borderTop: "1px solid rgba(201,168,76,0.15)" }}>
                  {c.title} — {fmtDate(c.start_time)}
                </div>
              ))}
          </div>
          <div style={cardStyle} data-testid="schedule-facilities-card">
            <div style={panelHeader}>Facilities ({facilities.length})</div>
            {facilities.map((f) => (
              <div key={f.id} style={{ fontSize: 12, padding: "5px 0", borderTop: "1px solid rgba(201,168,76,0.15)" }}>
                <span style={{ color: ENT_COLOR.gold }}>{f.name}</span> · {f.sport} · cap {f.capacity?.toLocaleString?.() || f.capacity}
              </div>
            ))}
          </div>
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §3 MedicalWorkflow
export const MedicalWorkflow = () => {
  const ATHLETE = "demo-uf-football-02";
  const [reports, setReports] = useState([]);
  const [error, setError] = useState(null);
  const [body, setBody] = useState({ athlete_id: ATHLETE, injury_type: "Muscle strain", body_part: "Hamstring", severity: "moderate", treatment_plan: "Rest + soft tissue therapy + return-to-run protocol." });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { const r = await medicalApi.injuryStatus(ATHLETE); setReports(r.reports || []); }
    catch (e) { setError("Sign in (trainer/admin) to view medical workflow."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to view medical workflow."); }, []);

  const submit = async () => {
    setBusy(true);
    try { await medicalApi.injuryReport(body); await refresh(); }
    catch (e) { setError(e?.response?.data?.detail?.detail || "Report failed (trainer/admin/athlete-self only)."); }
    finally { setBusy(false); }
  };

  const clear = async (injury_id) => {
    try { await medicalApi.rehabClearance({ injury_id, cleared: true, notes: "Return-to-play battery passed." }); await refresh(); }
    catch (e) { setError("Clearance requires trainer/admin role."); }
  };

  const stages = ["Reported", "Evaluated", "Treatment plan", "Rehab progress", "RTP cleared"];

  return (
    <EntPageShell title="Medical Workflow" testId="medical-workflow"
      lede="HIPAA-aware injury reporting, rehab progression, and return-to-play clearance. Every read/write writes an AuditLog with hipaa_flag and 6-year retention.">
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="medical-reports-card">
          <div style={panelHeader}>Open injury reports — {ATHLETE}</div>
          {error && <ErrorMsg message={error} />}
          {!error && reports.length === 0 && <Empty note="No injuries on file. Submit one to see the 5-stage timeline." />}
          {reports.map((r) => (
            <div key={r.id} data-testid="medical-injury-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "12px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.injury_type} · {r.body_part}</div>
                  <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)" }}>{fmtDate(r.date_reported)}</div>
                </div>
                <SeverityPill severity={r.severity === "severe" ? "critical" : r.severity === "moderate" ? "high" : "medium"} />
              </div>
              <div data-testid="medical-stage-tracker" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 6, marginTop: 10 }}>
                {stages.map((s, i) => {
                  const active = (r.stage || 1) >= i + 1;
                  return (
                    <div key={s} style={{ background: active ? ENT_COLOR.gold : "transparent", color: active ? ENT_COLOR.bg : "rgba(244,239,230,0.5)", border: `1px solid ${ENT_COLOR.gold}`, padding: "5px 6px", borderRadius: 3, fontFamily: "Cinzel, Georgia, serif", fontSize: 9, textAlign: "center", letterSpacing: 0.6 }}>
                      {i + 1}. {s}
                    </div>
                  );
                })}
              </div>
              {r.clearance_status === "open" && (
                <button data-testid="medical-clear-btn" style={{ ...goldBtn(true), marginTop: 10 }} onClick={() => clear(r.id)}>Mark RTP cleared</button>
              )}
              {r.clearance_status === "cleared" && (
                <div style={{ marginTop: 10, color: ENT_COLOR.green, fontSize: 11, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>
                  ✓ Cleared by {r.cleared_by} · {fmtDate(r.cleared_at)}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={cardStyle} data-testid="medical-report-form">
          <div style={panelHeader}>Report new injury</div>
          {["injury_type", "body_part", "severity", "treatment_plan"].map((k) => (
            <div key={k} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>{k}</label>
              <input value={body[k] || ""} onChange={(e) => setBody({ ...body, [k]: e.target.value })}
                style={{ width: "100%", padding: 8, marginTop: 4, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }} />
            </div>
          ))}
          <button data-testid="medical-submit-btn" style={goldBtn()} onClick={submit} disabled={busy}>Submit report</button>
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §6 ComplianceCenter
export const ComplianceCenterEnt = () => {
  const [eligibility, setEligibility] = useState([]);
  const [nil, setNil] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auth.token()) { setError("Sign in (compliance/admin) to view compliance."); return; }
    Promise.all([complianceApi.eligibility(), complianceApi.nilList(), complianceApi.deadlines()])
      .then(([e, n, d]) => { setEligibility(e.eligibility || []); setNil(n.disclosures || []); setDeadlines(d.deadlines || []); })
      .catch(() => setError("Could not load compliance data — requires compliance/admin role."));
  }, []);

  return (
    <EntPageShell title="Compliance Center" testId="compliance-center"
      lede="NCAA + FHSAA eligibility, NIL disclosures (Bylaw 12.5), academic progress, transfer status. Every action writes a FERPA-flagged AuditLog with 5-year retention.">
      {error && <ErrorMsg message={error} />}
      <div style={{ display: "grid", gap: 18 }}>
        <div style={cardStyle} data-testid="compliance-eligibility-card">
          <div style={panelHeader}>Academic eligibility ({eligibility.length})</div>
          {eligibility.length === 0 ? <Empty note="No academic eligibility records yet. POST /api/compliance/update with compliance_type=academic to create some." /> :
            eligibility.map((r) => (
              <div key={r.id} data-testid="compliance-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "8px 0", display: "flex", justifyContent: "space-between" }}>
                <span>{r.athlete_id}</span>
                <SeverityPill severity={r.status === "eligible" ? "low" : "high"} />
              </div>
            ))}
        </div>
        <div style={cardStyle} data-testid="compliance-nil-card">
          <div style={panelHeader}>NIL disclosures ({nil.length})</div>
          {nil.length === 0 ? <Empty note="No NIL disclosures filed. Use POST /api/compliance/nil/submit." /> :
            nil.map((r) => (
              <div key={r.id} data-testid="compliance-nil-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{r.athlete_id} · {r.brand_partner}</span>
                  <SeverityPill severity={r.approval_status === "approved" ? "low" : "medium"} />
                </div>
                <div style={{ fontSize: 11, color: "rgba(244,239,230,0.55)", marginTop: 2 }}>
                  {r.compensation_type} · ${r.estimated_value?.toLocaleString?.()} · {r.bylaw_reference}
                </div>
              </div>
            ))}
        </div>
        <div style={cardStyle} data-testid="compliance-deadlines-card">
          <div style={panelHeader}>Deadlines (next 30 days) — {deadlines.length}</div>
          {deadlines.length === 0 ? <Empty note="No imminent deadlines. Compliance is current." /> :
            deadlines.map((d) => (
              <div key={d.id} style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "6px 0", fontSize: 12 }}>
                {d.athlete_id} · {d.compliance_type} · {fmtDate(d.expiration_date)}
              </div>
            ))}
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §7 DocumentCenterEnt
export const DocumentCenterEnt = () => {
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(null);
  const [name, setName] = useState("waiver-2026.pdf");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const refresh = async () => {
    try { const r = await documentsApi.list(); setDocs(r.documents || []); }
    catch (e) { setError("Sign in to access documents."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to access documents."); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      await documentsApi.upload({ athlete_id: "demo-uf-football-01", document_type: "waiver", file_name: name });
      setToast(`Uploaded ${name}.`);
      await refresh();
    } catch (e) { setError("Upload failed."); }
    finally { setBusy(false); setTimeout(() => setToast(null), 2500); }
  };

  const approve = async (id) => {
    try { await documentsApi.approve({ document_id: id, approve: true, notes: "Approved by compliance." }); setToast("Document approved."); await refresh(); }
    catch (e) { setError("Approval requires admin/compliance role."); }
    finally { setTimeout(() => setToast(null), 2500); }
  };

  const sendForSignature = async (id) => {
    try {
      const env = await documentsApi.sign({ document_id: id, signer_email: "athlete@fsu.demo", signer_name: "Athlete Demo" });
      setToast(`Envelope sent — ${env.envelope_id} · ${env.status}`);
      await refresh();
    } catch (e) { setError("Signature send failed."); }
    finally { setTimeout(() => setToast(null), 4000); }
  };

  return (
    <EntPageShell title="Document Center" testId="document-center"
      lede="Upload, approve, and route documents through DocuSign envelopes. Every event is FERPA-logged with 5-year retention.">
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="documents-list-card">
          <div style={panelHeader}>Document library ({docs.length})</div>
          {error && <ErrorMsg message={error} />}
          {!error && docs.length === 0 && <Empty note="No documents uploaded yet." />}
          {docs.map((d) => (
            <div key={d.id} data-testid="documents-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 13 }}>{d.file_name} <span style={{ fontSize: 10, color: "rgba(244,239,230,0.55)", marginLeft: 6 }}>v{d.version}</span></div>
                  <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)" }}>
                    {d.athlete_id} · {d.document_type} · {fmtDate(d.uploaded_at)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <SeverityPill severity={d.status === "approved" ? "low" : d.status === "rejected" ? "critical" : "medium"} />
                  <span style={{ fontSize: 9, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>{d.signature_status}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {d.status !== "approved" && <button data-testid="documents-approve-btn" style={ghostBtn(true)} onClick={() => approve(d.id)}>Approve</button>}
                {d.signature_status === "unsigned" && <button data-testid="documents-sign-btn" style={ghostBtn(true)} onClick={() => sendForSignature(d.id)}>Send for signature</button>}
              </div>
            </div>
          ))}
        </div>
        <div style={cardStyle} data-testid="documents-upload-card">
          <div style={panelHeader}>Upload new document</div>
          <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>File name</label>
          <input data-testid="documents-name-input" value={name} onChange={(e) => setName(e.target.value)}
            style={{ width: "100%", padding: 8, marginTop: 4, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }} />
          <button data-testid="documents-upload-btn" style={{ ...goldBtn(), marginTop: 14 }} onClick={upload} disabled={busy}>Upload</button>
        </div>
      </div>
      {toast && (
        <div data-testid="documents-toast" style={{ position: "fixed", bottom: 20, right: 20, background: "#112244", border: `1.5px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, padding: "10px 16px", borderRadius: 6, fontSize: 12, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 0.6, zIndex: 9999 }}>
          {toast}
        </div>
      )}
    </EntPageShell>
  );
};

// ==================================================== §9 AlertCenterEnt
export const AlertCenterEnt = () => {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState(null);

  const refresh = async (sev) => {
    try { const r = await alertsEntApi.list(sev || null); setAlerts(r.alerts || []); }
    catch (e) { setError("Sign in to view alerts."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to view alerts."); }, []);

  const ack = async (id) => { await alertsEntApi.ack(id); await refresh(filter); };
  const clearAcked = async () => {
    try { await alertsEntApi.clear(); await refresh(filter); }
    catch (e) { setError("Clear requires admin/compliance role."); }
  };

  return (
    <EntPageShell title="Alert Center" testId="alert-center-ent"
      lede="Live alert feed across HealthIQ, Compliance, Scheduling, Injury, and Crawl modules. Severity routing: low→dashboard, medium→email, high→email+push, critical→email+push+SMS.">
      <div style={cardStyle} data-testid="alerts-feed-card">
        <div style={panelHeader}>
          <span>Active alerts ({alerts.length})</span>
          <span style={{ display: "flex", gap: 6 }}>
            {["", "low", "medium", "high", "critical"].map((s) => (
              <button key={s || "all"} data-testid={`alerts-filter-${s || "all"}`}
                style={(filter === s) ? goldBtn(true) : ghostBtn(true)}
                onClick={() => { setFilter(s); refresh(s); }}>
                {s || "All"}
              </button>
            ))}
            <button data-testid="alerts-clear-acked" style={ghostBtn(true)} onClick={clearAcked}>Clear acked</button>
          </span>
        </div>
        {error && <ErrorMsg message={error} />}
        {!error && alerts.length === 0 && <Empty note="No alerts in this severity tier." />}
        {alerts.map((a) => (
          <div key={a.id} data-testid="alerts-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</div>
                <div style={{ fontSize: 11, color: "rgba(244,239,230,0.6)", marginTop: 2 }}>{a.body}</div>
                <div style={{ fontSize: 10, color: "rgba(244,239,230,0.45)", marginTop: 2 }}>
                  {fmtDate(a.created_at)} · {a.kind}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <SeverityPill severity={a.severity} />
                {!a.ack && <button data-testid="alerts-ack-btn" style={ghostBtn(true)} onClick={() => ack(a.id)}>Acknowledge</button>}
                {a.ack && <span style={{ color: ENT_COLOR.green, fontSize: 10, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>✓ ACKED</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </EntPageShell>
  );
};
